"use client";

import { useEffect, useRef } from "react";

interface ScrollLockOptions {
  restoreScrollPosition?: boolean;
  lockClass?: string;
  preventTouchScroll?: boolean;
}

interface ScrollState {
  scrollY: number;
  scrollX: number;
  bodyOverflow: string;
  bodyPosition: string;
  bodyTop: string;
  bodyLeft: string;
  bodyWidth: string;
}

let activeModalCount = 0;
let originalScrollState: ScrollState | null = null;

/*
 * The touch listeners are held at module scope, not per hook instance.
 *
 * They were previously plain functions in the hook body, so every render made
 * new ones: addEventListener got render N's identity and removeEventListener
 * was handed render M's, which removes nothing. The touchmove handler then
 * stayed on `document` for the life of the page, calling preventDefault() on
 * every move outside `[data-modal-scrollable]` - which cancels touch scrolling
 * outright. One inactivity popup was enough to kill swiping on Android and iOS
 * for good, while desktop wheel and keyboard input carried on working.
 *
 * Module scope also fixes the cross-instance case: whichever hook drops the
 * count to zero removes the exact listeners that were attached, even if a
 * different instance attached them.
 */
let attachedTouchStart: ((_event: TouchEvent) => void) | null = null;
let attachedTouchMove: ((_event: TouchEvent) => void) | null = null;

export const useModalScrollLock = (
  isOpen: boolean,
  options: ScrollLockOptions = {},
) => {
  const {
    restoreScrollPosition = false,
    lockClass,
    preventTouchScroll = true,
  } = options;

  const isLockedRef = useRef(false);
  const touchStartY = useRef(0);

  const lockScroll = () => {
    if (typeof window === "undefined") return;

    const body = document.body;

    if (activeModalCount === 0) {
      originalScrollState = {
        scrollY: window.scrollY,
        scrollX: window.scrollX,
        bodyOverflow: body.style.overflow,
        bodyPosition: body.style.position,
        bodyTop: body.style.top,
        bodyLeft: body.style.left,
        bodyWidth: body.style.width,
      };

      body.style.overflow = "hidden";
      body.style.position = "fixed";
      body.style.top = `-${originalScrollState.scrollY}px`;
      body.style.left = `-${originalScrollState.scrollX}px`;
      body.style.width = "100%";

      if (lockClass) {
        body.classList.add(lockClass);
      }

      body.dataset.modalScrollLock = "true";

      if (preventTouchScroll) {
        attachedTouchStart = (event: TouchEvent) => {
          touchStartY.current = event.touches[0].clientY;
        };
        attachedTouchMove = (event: TouchEvent) => {
          const target = event.target as Element | null;
          if (!target?.closest("[data-modal-scrollable]")) {
            event.preventDefault();
          }
        };
        document.addEventListener("touchstart", attachedTouchStart, {
          passive: false,
        });
        document.addEventListener("touchmove", attachedTouchMove, {
          passive: false,
        });
      }
    }

    activeModalCount++;
    isLockedRef.current = true;
  };

  const unlockScroll = () => {
    if (typeof window === "undefined" || !isLockedRef.current) return;

    activeModalCount = Math.max(0, activeModalCount - 1);
    isLockedRef.current = false;

    if (activeModalCount === 0 && originalScrollState) {
      const body = document.body;

      body.style.overflow = originalScrollState.bodyOverflow;
      body.style.position = originalScrollState.bodyPosition;
      body.style.top = originalScrollState.bodyTop;
      body.style.left = originalScrollState.bodyLeft;
      body.style.width = originalScrollState.bodyWidth;

      if (lockClass) {
        body.classList.remove(lockClass);
      }

      delete body.dataset.modalScrollLock;

      if (restoreScrollPosition) {
        // Explicitly instant. globals.css sets
        // `* { scroll-behavior: smooth !important }`, so the two-argument
        // scrollTo() animates — and because dropping `position: fixed` snaps
        // the document back to the top first, the user watches the page glide
        // from the top all the way down to wherever the modal was opened.
        // Restoring a scroll position should never be a visible journey.
        window.scrollTo({
          left: originalScrollState.scrollX,
          top: originalScrollState.scrollY,
          behavior: "instant",
        });
      }

      // Removed unconditionally: `preventTouchScroll` may differ from the value
      // in force when the listeners were attached, and leaving a touchmove
      // handler behind disables scrolling for the rest of the session.
      if (attachedTouchStart) {
        document.removeEventListener("touchstart", attachedTouchStart);
        attachedTouchStart = null;
      }
      if (attachedTouchMove) {
        document.removeEventListener("touchmove", attachedTouchMove);
        attachedTouchMove = null;
      }

      originalScrollState = null;
    }
  };

  useEffect(() => {
    if (isOpen) {
      lockScroll();
    } else {
      unlockScroll();
    }

    return () => {
      if (isLockedRef.current) {
        unlockScroll();
      }
    };
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (isLockedRef.current) {
        unlockScroll();
      }
    };
  }, []);

  return {
    isLocked: isLockedRef.current,
    activeModalCount,
    lockScroll,
    unlockScroll,
  };
};

export const isScrollLocked = (): boolean => {
  if (typeof window === "undefined") return false;
  return document.body.dataset.modalScrollLock === "true";
};

export const getActiveModalCount = (): number => {
  return activeModalCount;
};

export const forceUnlockScroll = (): void => {
  if (typeof window === "undefined") return;

  const body = document.body;

  activeModalCount = 0;
  if (attachedTouchStart) {
    document.removeEventListener("touchstart", attachedTouchStart);
    attachedTouchStart = null;
  }
  if (attachedTouchMove) {
    document.removeEventListener("touchmove", attachedTouchMove);
    attachedTouchMove = null;
  }
  body.style.overflow = "";
  body.style.position = "";
  body.style.top = "";
  body.style.left = "";
  body.style.width = "";
  delete body.dataset.modalScrollLock;
  body.classList.remove("modal-scroll-locked", "scroll-locked");
  originalScrollState = null;
  console.warn(
    "Force unlocked all modal scroll locks. This should only be used in error recovery.",
  );
};

export default useModalScrollLock;
